var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getCurrentUser } from "./auth";
// Get WordPress REST API base URL
function getApiUrl() {
    const wpData = window.wpData;
    if (wpData === null || wpData === void 0 ? void 0 : wpData.apiUrl) {
        // Convert wp/v2/ to noads/v1/
        return wpData.apiUrl.replace('wp/v2/', 'noads/v1/');
    }
    // Fallback to default WordPress REST API URL
    return '/wp-json/noads/v1/';
}
// Get WordPress nonce for CSRF protection
function getNonce() {
    const wpData = window.wpData;
    return (wpData === null || wpData === void 0 ? void 0 : wpData.nonce) || '';
}
// Helper to make authenticated API requests
function apiRequest(endpoint_1) {
    return __awaiter(this, arguments, void 0, function* (endpoint, options = {}) {
        const url = getApiUrl() + endpoint;
        const nonce = getNonce();
        // Add cache-busting for GET requests
        const isGetRequest = !options.method || options.method === 'GET';
        const cacheBuster = isGetRequest ? `?t=${Date.now()}` : '';
        const defaultOptions = Object.assign({ credentials: 'include', cache: 'no-store', headers: Object.assign(Object.assign({ 'Content-Type': 'application/json', 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }, (nonce && { 'X-WP-Nonce': nonce })), options.headers) }, options);
        return fetch(url + cacheBuster, defaultOptions);
    });
}
// Helper to parse error responses
function parseErrorResponse(response) {
    if (response === null || response === void 0 ? void 0 : response.message) {
        return response.message;
    }
    if (response === null || response === void 0 ? void 0 : response.code) {
        return response.code;
    }
    if (typeof response === 'string') {
        return response;
    }
    return 'An error occurred. Please try again.';
}
// Convert API response to SavedRecipe format
function convertApiRecipeToSavedRecipe(apiRecipe) {
    var _a, _b, _c;
    const recipe = apiRecipe.recipe || {};
    const imageUrl = typeof recipe.image === 'string'
        ? recipe.image
        : ((_a = recipe.image) === null || _a === void 0 ? void 0 : _a.catalog) || ((_b = recipe.image) === null || _b === void 0 ? void 0 : _b.thumbnail) || ((_c = recipe.image) === null || _c === void 0 ? void 0 : _c.full) || '';
    return {
        id: apiRecipe.recipe_slug || apiRecipe.recipe_id || recipe.slug || recipe.recipe_id || '',
        slug: apiRecipe.recipe_slug,
        recipe_id: apiRecipe.recipe_id,
        title: recipe.title || '',
        description: recipe.summary || '',
        image: imageUrl,
        cookTime: recipe.cook_time_total_minutes ? `${recipe.cook_time_total_minutes} min` : '0 min',
        servings: recipe.yield_base_servings || 4,
        difficulty: recipe.flame || 1,
        category: recipe.category || 'other',
        savedAt: apiRecipe.saved_at || new Date().toISOString(),
        userNotes: apiRecipe.user_notes || undefined,
        userRating: apiRecipe.user_rating || undefined,
        tags: apiRecipe.tags || undefined,
        custom_category: apiRecipe.custom_category || undefined,
    };
}
export function getSavedRecipes() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                return [];
            }
            const response = yield apiRequest('user/recipes');
            if (!response.ok) {
                if (response.status === 401) {
                    // User not authenticated
                    return [];
                }
                throw new Error(`Failed to fetch recipes: ${response.status}`);
            }
            const data = yield response.json();
            const items = data.items || [];
            return items.map(convertApiRecipeToSavedRecipe);
        }
        catch (error) {
            console.error('Error fetching saved recipes:', error);
            return [];
        }
    });
}
export function saveRecipe(recipe) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                throw new Error('You must be logged in to save recipes');
            }
            const recipeSlug = recipe.slug || recipe.id;
            if (!recipeSlug) {
                throw new Error('Recipe slug is required');
            }
            const response = yield apiRequest('user/recipes', {
                method: 'POST',
                body: JSON.stringify({
                    recipe_slug: recipeSlug,
                    recipe_id: recipe.recipe_id,
                }),
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                if (response.status === 409) {
                    // Already in box
                    return true;
                }
                throw new Error(parseErrorResponse(errorData));
            }
            return true;
        }
        catch (error) {
            console.error('Error saving recipe:', error);
            throw error;
        }
    });
}
export function removeRecipe(recipeId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                throw new Error('You must be logged in to remove recipes');
            }
            // recipeId might be slug or recipe_id, try slug first
            const recipeSlug = recipeId;
            const response = yield apiRequest(`user/recipes/${encodeURIComponent(recipeSlug)}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                throw new Error(parseErrorResponse(errorData));
            }
            return true;
        }
        catch (error) {
            console.error('Error removing recipe:', error);
            throw error;
        }
    });
}
export function isRecipeSaved(recipeId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const recipes = yield getSavedRecipes();
            return recipes.some(r => r.id === recipeId || r.slug === recipeId || r.recipe_id === recipeId);
        }
        catch (error) {
            console.error('Error checking if recipe is saved:', error);
            return false;
        }
    });
}
export function updateRecipeNotes(recipeId, notes) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                throw new Error('You must be logged in to update notes');
            }
            const recipeSlug = recipeId;
            const response = yield apiRequest(`user/recipes/${encodeURIComponent(recipeSlug)}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    user_notes: notes,
                }),
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                throw new Error(parseErrorResponse(errorData));
            }
            return true;
        }
        catch (error) {
            console.error('Error updating recipe notes:', error);
            throw error;
        }
    });
}
export function updateRecipeRating(recipeId, rating) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                throw new Error('You must be logged in to update rating');
            }
            const recipeSlug = recipeId;
            const response = yield apiRequest(`user/recipes/${encodeURIComponent(recipeSlug)}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    user_rating: rating || null,
                }),
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                throw new Error(parseErrorResponse(errorData));
            }
            return true;
        }
        catch (error) {
            console.error('Error updating recipe rating:', error);
            throw error;
        }
    });
}
export function updateRecipeTags(recipeSlug, tags) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                throw new Error('You must be logged in to update tags');
            }
            const response = yield apiRequest(`user/recipes/${encodeURIComponent(recipeSlug)}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    tags: tags,
                }),
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                throw new Error(parseErrorResponse(errorData));
            }
            return true;
        }
        catch (error) {
            console.error('Error updating recipe tags:', error);
            throw error;
        }
    });
}
export function updateRecipeCategory(recipeSlug, category) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                throw new Error('You must be logged in to update category');
            }
            const response = yield apiRequest(`user/recipes/${encodeURIComponent(recipeSlug)}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    custom_category: category || null,
                }),
            });
            if (!response.ok) {
                const errorData = yield response.json().catch(() => ({}));
                throw new Error(parseErrorResponse(errorData));
            }
            return true;
        }
        catch (error) {
            console.error('Error updating recipe category:', error);
            throw error;
        }
    });
}
export function getUserCategories() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                return [];
            }
            const response = yield apiRequest('user/recipes/categories');
            if (!response.ok) {
                return [];
            }
            const data = yield response.json();
            return data.categories || [];
        }
        catch (error) {
            console.error('Error fetching user categories:', error);
            return [];
        }
    });
}
export function getUserTags() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if user is logged in
            const user = yield getCurrentUser();
            if (!user) {
                return [];
            }
            const response = yield apiRequest('user/recipes/tags');
            if (!response.ok) {
                return [];
            }
            const data = yield response.json();
            return data.tags || [];
        }
        catch (error) {
            console.error('Error fetching user tags:', error);
            return [];
        }
    });
}
export function getSavedRecipe(recipeId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const recipes = yield getSavedRecipes();
            return recipes.find(r => r.id === recipeId || r.slug === recipeId || r.recipe_id === recipeId);
        }
        catch (error) {
            console.error('Error getting saved recipe:', error);
            return undefined;
        }
    });
}
